import type { ComponentProps } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import PriceRowFlightPackage from 'LuxKit/PricePoints/PriceRowFlightPackage'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import AgentCommissionPanel from 'agentHub/components/AgentCommissionPanel'
import * as Analytics from 'analytics/analytics'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import WalledContent from 'components/Common/WalledContent'
import Group from 'components/utils/Group'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import {
  generateLoyaltyPointsCalculatorPropertyOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import {
  arePropertyRatesLoading,
  getPropertyCheapestRates,
  getPropertyOfferMapping,
  getPropertyRatesKey,
} from 'selectors/offerPage/offerPageSelectors'

const SummaryPane = styled(Pane)`
  position: relative;
`

interface Props {
  property: App.Property.Property
  isPropertyUltraLux: boolean
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  lpcOffer: App.HotelOffer
  duration: number
  regionCode: string
  areAnytimeRates: boolean
  shouldShowAnytimeRate: boolean
  isLoading: boolean
  cheapestPropertyRates: [App.PropertyRate | App.Property.AnytimeRate | undefined, App.PropertyRate | undefined]
  searchId: string | undefined
  /** TODO for styled media queries */
  className?: string
  onViewPackageOptions: () => void
}

function PropertySummary({
  property,
  isPropertyUltraLux,
  lpcOffer,
  duration,
  regionCode,
  areAnytimeRates,
  shouldShowAnytimeRate,
  isLoading,
  cheapestPropertyRates,
  searchId,
  className,
  onViewPackageOptions,
}: Props) {
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)

  const onSignUpOrLoginClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const [hotelOnlyRate, hotelWithFlightsRate] = cheapestPropertyRates

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (!(hotelOnlyRate && 'service' in hotelOnlyRate)) return [undefined]
    return [
      generateLoyaltyPointsCalculatorPropertyOptions(hotelOnlyRate, {
        propertyCountryCode: property.location.countryCode,
        currentRegionCode: regionCode,
        isUltraLux: isPropertyUltraLux,
      }),
    ]
  }, [hotelOnlyRate, property.location.countryCode, regionCode, isPropertyUltraLux])

  const memberDiscount = hotelOnlyRate?.luxPlusTotals?.discount ?? 0
  const nonMemberDiscount = hotelOnlyRate?.totals.discount ?? 0
  const discount = memberDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? memberDiscount : nonMemberDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? nonMemberDiscount : undefined

  const hasTrackedPrice = useRef(false)
  const hotelOnlyDisplayPrice = hotelOnlyRate?.totals.displayPrice
  useEffect(() => {
    if (searchId && !isLoading && !hasTrackedPrice.current) {
      if (hotelOnlyDisplayPrice) {
        Analytics.trackClientEvent({
          subject: `searchId:${searchId}_offerId:${lpcOffer.id}`,
          action: hotelOnlyDisplayPrice.toString(),
          category: 'price_accuracy',
          type: 'operational',
        })
      }
      // If we're not loading and there's no cheapest price, it's likely that we don't have dates
      // set (i.e. we arrived via an anytime search result). Since we're not displaying initial
      // rates, we must refrain from firing the pricing accuracy event when a cheapest rate _does_
      // become available.
      //
      // For the case where dates _are_ set, we're relying on clean transitions (i.e. no gaps due to
      // races) from `isLoading` being set to `hotelOnlyRate` being available.
      hasTrackedPrice.current = true
    }
  }, [searchId, isLoading, hotelOnlyDisplayPrice, lpcOffer])

  const hideAnytimePrices = areAnytimeRates && !shouldShowAnytimeRate
  const onCta = useCallback(() => {
    if (hideAnytimePrices) {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    } else {
      onViewPackageOptions()
    }
  }, [hideAnytimePrices, dispatch, onViewPackageOptions])

  const ctaText = hideAnytimePrices
    ? intl.formatMessage({
        id: 'RuM40FaLnPgzb1r61ORb',
        defaultMessage: 'Select dates',
        description: 'CTA text when dates need to be selected on property summary',
      })
    : intl.formatMessage({
        id: 'IwfaJyEAJ7wPj6tL7uSA',
        defaultMessage: 'View package options',
        description: 'CTA text to view package options on property summary',
      })

  const agentHubCommissionPanelProps = useMemo<ComponentProps<typeof AgentCommissionPanel> | undefined>(() => {
    // NB: We don't have enough information on anytime rates to accurately determine a commission
    // rate. However, we also no longer show anytime rates (outside of paid marketing traffic), so
    // this isn't really a problem.
    if (areAnytimeRates || !(hotelOnlyRate && 'service' in hotelOnlyRate)) return undefined
    // NB: Because `<AgentCommissionPanel/>` doesn't use min/max commission rates, we don't need to
    // worry about aggregating these across all active offers linked to this property.
    if (hotelOnlyRate.service.type === 'lpc') {
      return {
        offerId: hotelOnlyRate.service.offerId,
        packageId: hotelOnlyRate.service.packageId,
        offerTotal: hotelOnlyRate.totals.sellingPrice,
        retailValue: hotelOnlyRate.totals.valuedUpToPrice,
        memberPrice: hotelOnlyRate.luxPlusTotals?.sellingPrice,
      }
    }
    if (hotelOnlyRate.service.type === 'bedbank') {
      return {
        offerId: hotelOnlyRate.service.propertyId,
        packageId: hotelOnlyRate.service.rateId,
        offerTotal: hotelOnlyRate.totals.sellingPrice,
        retailValue: hotelOnlyRate.totals.valuedUpToPrice,
        memberPrice: hotelOnlyRate.luxPlusTotals?.sellingPrice,
      }
    }
  }, [areAnytimeRates, hotelOnlyRate])

  return <SummaryPane border="neutral-six" borderRadius="S" padding="28 20" shadow="bottom-small" className={className}>
    <WalledContent
      enforced={lpcOffer.walledGarden}
      wall={
        <Group direction="vertical" gap={16}>
          {/* The following is based on `<GenericLoginSignupWall/>`, used by `<PropertyStickyHeader/>`. */}
          <BodyTextBlock
            variant="medium"
            weight="bold"
            startIcon={<LineInfoCircleIcon />}
          >
            {intl.formatMessage({
              id: 'w-jCEfeoOuAa6oxdoAmk',
              defaultMessage: 'Join now to view member prices',
              description: 'Wall message for non-members on the property summary',
            })}
          </BodyTextBlock>
          <Group direction="vertical" gap={8}>
            <TextButton kind="primary" onClick={onSignUpOrLoginClick}>
              {intl.formatMessage({
                id: 'jriEHMIML1WtgYOIfQng',
                defaultMessage: 'Sign up for free',
                description: 'Sign up button text on property summary on the login or signup page',
              })}
            </TextButton>
            <TextButton kind="secondary" onClick={onSignUpOrLoginClick}>
              {intl.formatMessage({
                id: 'D_4hSdWDoUx1sMPc11fp',
                defaultMessage: 'Log in',
                description: 'Login button text on property summary on the login or signup page',
              })}
            </TextButton>
          </Group>
        </Group>
      }
    >
      <Group direction="vertical" gap={12}>
        {!hideAnytimePrices && <Group direction="vertical" horizontalAlign="center" gap={8}>
          <Group direction="vertical" horizontalAlign="center" gap={4}>
            <Group direction="vertical" horizontalAlign="center">
              <LoadingIndicator floating visible={isLoading} opaque backdrop="white" />
              {/* TODO offerConfig from `lpcOffer.luxPlus`? */}
              {hotelOnlyRate && <>
                <PriceRowPriceCaption>
                  {intl.formatMessage(
                    {
                      id: 'aB2hOZ7E9REZ6eGA0GLL',
                      defaultMessage: '{count, plural, one {# night} other {# nights}} from',
                      description: 'Duration in nights for the property summary pricing',
                    },
                    { count: duration },
                  )}
                </PriceRowPriceCaption>
                <LuxPlusPriceStack
                  size="L"
                  price={hotelOnlyRate.totals.displayPrice}
                  memberPrice={hotelOnlyRate.luxPlusTotals?.displayPrice}
                  saleUnit="total"
                  testid="hotel-summary-price"
                  horizontalAlign="center"
                />
                <PriceRowTaxesAndFees />
                {/* TODO price breakdown for above? */}
                {/* TODO extra guest surcharges? */}
                <PriceRowValueDiscountWithCaption
                  size="M"
                  originalValue={hotelOnlyRate.luxPlusTotals?.valuedUpToPrice ?? hotelOnlyRate.totals.valuedUpToPrice}
                  discountPercentage={discount}
                />
              </>}
            </Group>
            {hotelOnlyRate && <LoyaltyPointsEarn
              horizontalAlign="center"
              offer={lpcOffer}
              calculationType="estimate"
              calculationRequests={pointsEarnCalculationRequests}
            />}
            {hotelWithFlightsRate?.includesFlights && <Group direction="vertical" horizontalAlign="center">
              {typeof hotelWithFlightsRate.cheapestFlightPrice === 'number' && hotelWithFlightsRate.cheapestFlightPrice > 0 && <PriceRowFlightPackage
                product="hotel"
                price={hotelWithFlightsRate.luxPlusTotals?.displayPrice ?? hotelWithFlightsRate.totals.displayPrice}
              />}
            </Group>}
          </Group>
          {agentHubCommissionPanelProps && <AgentCommissionPanel {...agentHubCommissionPanelProps} />}
        </Group>}
        {hideAnytimePrices && <BodyTextBlock
          variant="medium"
          weight="bold"
          startIcon={<LineInfoCircleIcon />}
        >
          {intl.formatMessage({
            id: 'OXRZ6EfZFR8Pi9Z9xOmg',
            defaultMessage: 'Select dates to get the best price for this offer',
            description: 'Info message when dates aren’t selected on property summary',
          })}
        </BodyTextBlock>}
        <TextButton onClick={onCta} kind="primary" fit="flex">
          {ctaText}
        </TextButton>
      </Group>
    </WalledContent>
  </SummaryPane>
}

export default withOfferPageAndReduxState((state, appState) => ({
  isPropertyUltraLux: getPropertyOfferMapping(state, appState)?.isUltraLux ?? false,
  duration: state.duration,
  regionCode: appState.geo.currentRegionCode,
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  // We only need to show anytime pricing for traffic coming from marketing, which will have
  // `utm_bp` set.
  shouldShowAnytimeRate: !!appState.utm.bp,
  isLoading: arePropertyRatesLoading(state, appState),
  cheapestPropertyRates: getPropertyCheapestRates(state, appState),
  searchId: appState.router.location.query.searchId,
  open: !!state.activeFilter,
}), PropertySummary, preventRenderWithOpenFilter)
